import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import * as api from '../../api/PurchaseAPI/newStockTransferAPI';

const err = (e, fallback) => e?.Message || e?.message || fallback;

export const fetchFromCCList = createAsyncThunk('newStockTransfer/fetchFromCCList', async ({ uid, rid }, { rejectWithValue }) => {
    try { const res = await api.getFromCCList(uid, rid); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch From Cost Centres')); }
});

export const fetchRecievedCCList = createAsyncThunk('newStockTransfer/fetchRecievedCCList', async ({ uid, rid }, { rejectWithValue }) => {
    try { const res = await api.getRecievedCCList(uid, rid); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch To Cost Centres')); }
});

export const fetchCategories = createAsyncThunk('newStockTransfer/fetchCategories', async (_, { rejectWithValue }) => {
    try { const res = await api.getCategories(); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch categories')); }
});

export const fetchAutoCompleteItem = createAsyncThunk('newStockTransfer/fetchAutoCompleteItem', async (params, { rejectWithValue }) => {
    try { const res = await api.autoCompleteItem(params); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to search items')); }
});

export const fetchTempDetails = createAsyncThunk('newStockTransfer/fetchTempDetails', async ({ itemcode, cccode }, { rejectWithValue }) => {
    try { const res = await api.getTempDetails(itemcode, cccode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch item lot details')); }
});

export const saveTempItems = createAsyncThunk('newStockTransfer/saveTempItems', async (payload, { rejectWithValue }) => {
    try { const res = await api.saveTempItems(payload); return res?.Data; }
    catch (e) { return rejectWithValue(err(e, 'Failed to add item to transfer')); }
});

export const fetchCartGrid = createAsyncThunk('newStockTransfer/fetchCartGrid', async (cccode, { rejectWithValue }) => {
    try { const res = await api.getCartGrid(cccode); return res?.Data || []; }
    catch (e) { return rejectWithValue(err(e, 'Failed to fetch transfer cart')); }
});

export const deleteCartItem = createAsyncThunk('newStockTransfer/deleteCartItem', async (payload, { rejectWithValue }) => {
    try {
        const res = await api.deleteCartItem(payload);
        // SP returns 'Submited' or the SQL error text (HTTP 200 either way).
        if (res?.Data !== 'Submited') return rejectWithValue(res?.Data || 'Failed to delete item');
        return res.Data;
    }
    catch (e) { return rejectWithValue(err(e, 'Failed to delete item')); }
});

export const saveTransfer = createAsyncThunk('newStockTransfer/saveTransfer', async (payload, { rejectWithValue }) => {
    try { const res = await api.saveTransfer(payload); return res?.Data; }
    catch (e) { return rejectWithValue(err(e, 'Failed to submit transfer')); }
});

const initialState = {
    fromCCList: [], recCCList: [], categoryList: [],
    suggestions: [], suggestionsLoading: false,
    tempItems: [], tempLoading: false,
    cartItems: [], cartLoading: false,
    loading: { fromCC: false, recCC: false, categories: false, saveTemp: false, delete: false, submit: false },
    errors: { fromCC: null, recCC: null, categories: null, temp: null, saveTemp: null, cart: null, delete: null, submit: null },
    saveTempResult: null,
    submitResult: null,
};

const newStockTransferSlice = createSlice({
    name: 'newStockTransfer',
    initialState,
    reducers: {
        clearSuggestions: (s) => { s.suggestions = []; },
        clearTempItems: (s) => { s.tempItems = []; s.errors.temp = null; },
        clearSaveTempResult: (s) => { s.saveTempResult = null; s.errors.saveTemp = null; },
        clearSubmitResult: (s) => { s.submitResult = null; s.errors.submit = null; },
        resetNewStockTransfer: () => initialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchFromCCList.pending, (s) => { s.loading.fromCC = true; })
            .addCase(fetchFromCCList.fulfilled, (s, a) => { s.loading.fromCC = false; s.fromCCList = a.payload; })
            .addCase(fetchFromCCList.rejected, (s, a) => { s.loading.fromCC = false; s.errors.fromCC = a.payload; });

        builder
            .addCase(fetchRecievedCCList.pending, (s) => { s.loading.recCC = true; })
            .addCase(fetchRecievedCCList.fulfilled, (s, a) => { s.loading.recCC = false; s.recCCList = a.payload; })
            .addCase(fetchRecievedCCList.rejected, (s, a) => { s.loading.recCC = false; s.errors.recCC = a.payload; });

        builder
            .addCase(fetchCategories.pending, (s) => { s.loading.categories = true; })
            .addCase(fetchCategories.fulfilled, (s, a) => { s.loading.categories = false; s.categoryList = a.payload; })
            .addCase(fetchCategories.rejected, (s, a) => { s.loading.categories = false; s.errors.categories = a.payload; });

        builder
            .addCase(fetchAutoCompleteItem.pending, (s) => { s.suggestionsLoading = true; })
            .addCase(fetchAutoCompleteItem.fulfilled, (s, a) => { s.suggestionsLoading = false; s.suggestions = a.payload; })
            .addCase(fetchAutoCompleteItem.rejected, (s) => { s.suggestionsLoading = false; s.suggestions = []; });

        builder
            .addCase(fetchTempDetails.pending, (s) => { s.tempLoading = true; s.errors.temp = null; })
            .addCase(fetchTempDetails.fulfilled, (s, a) => { s.tempLoading = false; s.tempItems = a.payload; })
            .addCase(fetchTempDetails.rejected, (s, a) => { s.tempLoading = false; s.errors.temp = a.payload; s.tempItems = []; });

        builder
            .addCase(saveTempItems.pending, (s) => { s.loading.saveTemp = true; s.errors.saveTemp = null; })
            .addCase(saveTempItems.fulfilled, (s, a) => { s.loading.saveTemp = false; s.saveTempResult = a.payload; })
            .addCase(saveTempItems.rejected, (s, a) => { s.loading.saveTemp = false; s.errors.saveTemp = a.payload; });

        builder
            .addCase(fetchCartGrid.pending, (s) => { s.cartLoading = true; s.errors.cart = null; })
            .addCase(fetchCartGrid.fulfilled, (s, a) => { s.cartLoading = false; s.cartItems = a.payload; })
            .addCase(fetchCartGrid.rejected, (s, a) => { s.cartLoading = false; s.errors.cart = a.payload; s.cartItems = []; });

        builder
            .addCase(deleteCartItem.pending, (s) => { s.loading.delete = true; s.errors.delete = null; })
            .addCase(deleteCartItem.fulfilled, (s) => { s.loading.delete = false; })
            .addCase(deleteCartItem.rejected, (s, a) => { s.loading.delete = false; s.errors.delete = a.payload; });

        builder
            .addCase(saveTransfer.pending, (s) => { s.loading.submit = true; s.errors.submit = null; })
            .addCase(saveTransfer.fulfilled, (s, a) => { s.loading.submit = false; s.submitResult = a.payload; })
            .addCase(saveTransfer.rejected, (s, a) => { s.loading.submit = false; s.errors.submit = a.payload; });
    },
});

export const {
    clearSuggestions, clearTempItems, clearSaveTempResult, clearSubmitResult, resetNewStockTransfer,
} = newStockTransferSlice.actions;

export const selectNSTFromCCList = (s) => s.newStockTransfer.fromCCList;
export const selectNSTRecCCList = (s) => s.newStockTransfer.recCCList;
export const selectNSTCategoryList = (s) => s.newStockTransfer.categoryList;
export const selectNSTSuggestions = (s) => s.newStockTransfer.suggestions;
export const selectNSTSuggestionsLoading = (s) => s.newStockTransfer.suggestionsLoading;
export const selectNSTTempItems = (s) => s.newStockTransfer.tempItems;
export const selectNSTTempLoading = (s) => s.newStockTransfer.tempLoading;
export const selectNSTCartItems = (s) => s.newStockTransfer.cartItems;
export const selectNSTCartLoading = (s) => s.newStockTransfer.cartLoading;
export const selectNSTLoading = (s) => s.newStockTransfer.loading;
export const selectNSTErrors = (s) => s.newStockTransfer.errors;
export const selectNSTSaveTempResult = (s) => s.newStockTransfer.saveTempResult;
export const selectNSTSubmitResult = (s) => s.newStockTransfer.submitResult;

export default newStockTransferSlice.reducer;
